import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { addDoc, collection, doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  getStorage,
  ref as storageRef,
  deleteObject,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

export default function SeguimientoPostulacionDialog({ open, onClose, postulacion }) {
  const [seguimiento, setSeguimiento] = useState({
    entrevista: {
      realizada: false,
      reporte: "",
      aprobado: false,
      enlace: "",
      fecha: null,
      infoTexto: "",
    },
    documentacion: {
      recibida: false,
      verificada: false,
      observaciones: "",
    },
    estado: "en proceso",
  });
  const [seguimientoId, setSeguimientoId] = useState(null);
  const [inputEnlace, setInputEnlace] = useState("");
  const [infoReunion, setInfoReunion] = useState(null);

  const storage = getStorage();

  // Función auxiliar para extraer la ruta del archivo desde la URL de descarga
  const getFilePathFromUrl = (url) => {
    const baseUrl = "https://firebasestorage.googleapis.com/v0/b/clinica-de-la-costa.appspot.com/o/";
    if (url.startsWith(baseUrl)) {
      let filePath = url.substring(baseUrl.length);
      const index = filePath.indexOf("?");
      if (index !== -1) {
        filePath = filePath.substring(0, index);
      }
      return decodeURIComponent(filePath);
    }
    return url;
  };

  useEffect(() => {
    const fetchSeguimiento = async () => {
      if (postulacion && postulacion.seguimiento?.id) {
        setSeguimientoId(postulacion.seguimiento.id);
        const seguimientoRef = doc(
          db,
          "postulaciones",
          postulacion.id,
          "seguimiento",
          postulacion.seguimiento.id
        );
        try {
          const seguimientoDoc = await getDoc(seguimientoRef);
          if (seguimientoDoc.exists()) {
            const data = seguimientoDoc.data();
            if (data.entrevista.fecha) {
              data.entrevista.fecha = data.entrevista.fecha.toDate();
            }
            setSeguimiento(data);
            if (data.entrevista.enlace) {
              setInputEnlace(data.entrevista.enlace);
            }
            if (data.entrevista.infoTexto) {
              setInputEnlace(data.entrevista.infoTexto);
              parsearInfoTexto(data.entrevista.infoTexto);
            }
          } else {
            setSeguimiento({
              entrevista: {
                realizada: false,
                reporte: "",
                aprobado: false,
                enlace: "",
                fecha: null,
                infoTexto: "",
              },
              documentacion: {
                recibida: false,
                verificada: false,
                observaciones: "",
              },
              estado: "en proceso",
            });
          }
        } catch (error) {
          console.error("Error al obtener el seguimiento:", error);
        }
      }
    };

    fetchSeguimiento();
  }, [postulacion]);

  useEffect(() => {
    const guardarSeguimiento = async () => {
      if (postulacion && postulacion.id && seguimientoId) {
        const seguimientoRef = collection(
          db,
          "postulaciones",
          postulacion.id,
          "seguimiento"
        );
        const entrevistaDataParaGuardar = {
          ...seguimiento.entrevista,
          infoTexto: inputEnlace,
        };
        try {
          const seguimientoDocRef = doc(seguimientoRef, seguimientoId);
          await updateDoc(seguimientoDocRef, {
            ...seguimiento,
            entrevista: entrevistaDataParaGuardar,
          });
          // Actualizamos el campo "estado" en el documento principal de la postulación
          const postulacionRef = doc(db, "postulaciones", postulacion.id);
          await updateDoc(postulacionRef, { 
            estado: seguimiento.estado
          });
        } catch (error) {
          console.error("Error al actualizar el seguimiento:", error);
        }
      }
    };

    if (seguimiento.estado !== "en proceso") {
      guardarSeguimiento();
    }
  }, [seguimiento, postulacion, seguimientoId]);

  const handleInputChange = (campo, valor) => {
    const [campoPrincipal, campoAnidado] = campo.split(".");
    setSeguimiento((prevSeguimiento) => ({
      ...prevSeguimiento,
      [campoPrincipal]: {
        ...prevSeguimiento[campoPrincipal],
        [campoAnidado]: valor,
      },
    }));
  };

  const handleCheckboxChange = (campo) => {
    const [campoPrincipal, campoAnidado] = campo.split(".");
    setSeguimiento((prevSeguimiento) => ({
      ...prevSeguimiento,
      [campoPrincipal]: {
        ...prevSeguimiento[campoPrincipal],
        [campoAnidado]: !prevSeguimiento[campoPrincipal][campoAnidado],
      },
    }));
  };

  const handleEnviarCorreoContratado = async () => {
    try {
      const response = await fetch(
        "https://us-central1-clinica-de-la-costa.cloudfunctions.net/enviarCorreoContratado",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            correoDestino: postulacion.informacionPersonal.correoElectronico,
            nombrePostulante: postulacion.informacionPersonal.nombresApellidos,
          }),
        }
      );
      if (response.ok) {
        console.log("Correo de contratación enviado correctamente");
        alert("Se ha enviado un correo al postulante notificando su contratación.");
      } else {
        throw new Error("Error al enviar el correo de contratación");
      }
    } catch (error) {
      console.error("Error al enviar el correo de contratación:", error);
      alert("Hubo un error al enviar el correo de contratación. Por favor, inténtalo de nuevo.");
    }
  };

  const handleEnviarCorreoNoContinuar = async () => {
    try {
      const response = await fetch(
        "https://us-central1-clinica-de-la-costa.cloudfunctions.net/enviarCorreoNoContinuar",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            correoDestino: postulacion.informacionPersonal.correoElectronico,
            nombrePostulante: postulacion.informacionPersonal.nombresApellidos,
          }),
        }
      );
      if (response.ok) {
        console.log("Correo de no continuación enviado correctamente");
        alert("Se ha enviado un correo al postulante notificando que no continúa en el proceso.");
      } else {
        throw new Error("Error al enviar el correo de no continuación");
      }
    } catch (error) {
      console.error("Error al enviar el correo de no continuación:", error);
      alert("Hubo un error al enviar el correo de no continuación. Por favor, inténtalo de nuevo.");
    }
  };

  const handleContratar = async () => {
    setSeguimiento((prevSeguimiento) => ({
      ...prevSeguimiento,
      estado: "contratado",
    }));
    handleEnviarCorreoContratado();
  };

  const handleNoContinuar = async () => {
    setSeguimiento((prevSeguimiento) => ({
      ...prevSeguimiento,
      estado: "no contratado",
    }));
    handleEnviarCorreoNoContinuar();
  };

  const handleSaveSeguimiento = async () => {
    if (postulacion && postulacion.id) {
      const seguimientoRef = collection(
        db,
        "postulaciones",
        postulacion.id,
        "seguimiento"
      );
      try {
        if (seguimientoId) {
          const seguimientoDocRef = doc(seguimientoRef, seguimientoId);
          await updateDoc(seguimientoDocRef, seguimiento);
        } else {
          const nuevoSeguimientoRef = await addDoc(seguimientoRef, seguimiento);
          await updateDoc(doc(db, "postulaciones", postulacion.id), {
            seguimiento: { id: nuevoSeguimientoRef.id },
          });
          setSeguimientoId(nuevoSeguimientoRef.id);
        }
      } catch (error) {
        console.error("Error al actualizar el seguimiento:", error);
      }
    }
  };

  const handleAsignarEntrevista = () => {
    window.open("https://meet.google.com/", "_blank");
  };

  const parsearInfoTexto = (texto) => {
    const info = {};
    if (!texto) {
      setInfoReunion(null);
      return;
    }
    const tituloRegex = /^(.*?)(?=(Lunes|Martes|Miércoles|Jueves|Viernes|Sábado|Domingo),\s*\d{1,2}\s+de\s+[A-Za-z]+)/s;
    const tituloMatch = texto.match(tituloRegex);
    if (tituloMatch) {
      info.titulo = tituloMatch[1].trim();
    } else {
      info.titulo = texto.split("\n")[0].trim();
    }
    const fechaRegex = /(Lunes|Martes|Miércoles|Jueves|Viernes|Sábado|Domingo),\s*(\d{1,2}\s+de\s+[A-Za-z]+)/;
    const zonaHorariaRegex = /Zona horaria: (.*)$/m;
    const enlaceRegex = /Enlace de la videollamada: (https?:\/\/[^\s]+)/;
    const fechaMatch = texto.match(fechaRegex);
    const zonaHorariaMatch = texto.match(zonaHorariaRegex);
    const enlaceMatch = texto.match(enlaceRegex);
    if (fechaMatch) info.fechaTexto = fechaMatch[0].trim();
    if (zonaHorariaMatch) info.zonaHoraria = zonaHorariaMatch[1].trim();
    if (enlaceMatch) info.enlace = enlaceMatch[1].trim();
    setInfoReunion(info);
  };

  const handleEnlaceChange = (event) => {
    const texto = event.target.value;
    setInputEnlace(texto);
    parsearInfoTexto(texto);
    const fecha = extraerFechaHora(texto);
    setSeguimiento((prevSeguimiento) => ({
      ...prevSeguimiento,
      entrevista: {
        ...prevSeguimiento.entrevista,
        enlace: texto.includes("https://meet.google.com/")
          ? texto
          : prevSeguimiento.entrevista.enlace,
        fecha: fecha || prevSeguimiento.entrevista.fecha,
        infoTexto: texto,
      },
    }));
  };

  const extraerFechaHora = (texto) => {
    const fechaRegex = /(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/;
    const match = texto.match(fechaRegex);
    if (match) {
      const [, year, month, day, hours, minutes, seconds] = match;
      const fecha = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
      return fecha;
    }
    return null;
  };

  const handleEnviarEntrevista = async () => {
    try {
      const response = await fetch(
        "https://us-central1-clinica-de-la-costa.cloudfunctions.net/crearEntrevista",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            postulacion,
            fechaCita: seguimiento.entrevista.fecha,
            infoReunion,
          }),
        }
      );
      if (response.ok) {
        const data = await response.json();
        console.log("Correo enviado correctamente:", data);
        alert("La información de la entrevista ha sido enviada por correo electrónico.");
      } else {
        throw new Error("Error al enviar la entrevista por correo");
      }
    } catch (error) {
      console.error("Error al enviar la entrevista:", error);
      alert("Hubo un error al enviar la entrevista. Por favor, inténtalo de nuevo.");
    }
  };

  const handleEnviarSolicitud = async () => {
    try {
      const response = await fetch(
        "https://us-central1-clinica-de-la-costa.cloudfunctions.net/enviarSolicitudDocumentacion",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            observaciones: seguimiento.documentacion.observaciones,
            postulacionId: postulacion.id,
            correoDestino: postulacion.informacionPersonal.correoElectronico,
            nombrePostulante: postulacion.informacionPersonal.nombresApellidos,
          }),
        }
      );
      if (response.ok) {
        const data = await response.json();
        console.log("Solicitud enviada correctamente:", data);
        alert("La solicitud de documentación ha sido enviada por correo electrónico.");
      } else {
        throw new Error("Error al enviar la solicitud por correo");
      }
    } catch (error) {
      console.error("Error al enviar la solicitud:", error);
      alert("Hubo un error al enviar la solicitud. Por favor, inténtalo de nuevo.");
    }
  };

  // Funciones para manejo de archivos (CV y Certificados)
  // Se asume que los documentos subidos se encuentran dentro de "archivos" en la postulación
  const handleDeleteCV = async () => {
    try {
      if (!(postulacion.archivos && postulacion.archivos.cvUrl)) return;
      const filePath = getFilePathFromUrl(postulacion.archivos.cvUrl);
      const fileRef = storageRef(storage, filePath);
      await deleteObject(fileRef);
      await updateDoc(doc(db, "postulaciones", postulacion.id), { "archivos.cvUrl": "" });
      alert("CV eliminado correctamente");
    } catch (error) {
      console.error("Error al eliminar el CV:", error);
      alert("Error al eliminar el CV");
    }
  };

  const handleUpdateCV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      const newFilePath = `postulaciones/${postulacion.id}/cv/${file.name}`;
      const fileRef = storageRef(storage, newFilePath);
      const snapshot = await uploadBytes(fileRef, file);
      const newDownloadUrl = await getDownloadURL(snapshot.ref);
      await updateDoc(doc(db, "postulaciones", postulacion.id), { "archivos.cvUrl": newDownloadUrl });
      alert("CV actualizado correctamente");
    } catch (error) {
      console.error("Error al actualizar el CV:", error);
      alert("Error al actualizar el CV");
    }
  };

  const handleDeleteCertificado = async (index) => {
    try {
      const certificados =
        postulacion.archivos &&
        postulacion.archivos.certificadosUrls
          ? postulacion.archivos.certificadosUrls
          : [];
      const urlToDelete = certificados[index];
      if (!urlToDelete) return;
      const filePath = getFilePathFromUrl(urlToDelete);
      const fileRef = storageRef(storage, filePath);
      await deleteObject(fileRef);
      const nuevosCertificados = [...certificados];
      nuevosCertificados.splice(index, 1);
      await updateDoc(doc(db, "postulaciones", postulacion.id), {
        "archivos.certificadosUrls": nuevosCertificados,
      });
      alert("Certificado eliminado correctamente");
    } catch (error) {
      console.error("Error al eliminar el certificado:", error);
      alert("Error al eliminar el certificado");
    }
  };

  const handleUpdateCertificado = async (index, event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      const newFilePath = `postulaciones/${postulacion.id}/certificados/${file.name}`;
      const fileRef = storageRef(storage, newFilePath);
      const snapshot = await uploadBytes(fileRef, file);
      const newDownloadUrl = await getDownloadURL(snapshot.ref);
      const certificados =
        postulacion.archivos &&
        postulacion.archivos.certificadosUrls
          ? postulacion.archivos.certificadosUrls
          : [];
      const nuevosCertificados = [...certificados];
      nuevosCertificados[index] = newDownloadUrl;
      await updateDoc(doc(db, "postulaciones", postulacion.id), {
        "archivos.certificadosUrls": nuevosCertificados,
      });
      alert("Certificado actualizado correctamente");
    } catch (error) {
      console.error("Error al actualizar el certificado:", error);
      alert("Error al actualizar el certificado");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      {postulacion && (
        <>
          <DialogTitle>
            Seguimiento de {postulacion.informacionPersonal.nombresApellidos}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Entrevista
                </Typography>
                {!seguimiento.entrevista.infoTexto && (
                  <Button variant="outlined" color="primary" onClick={handleAsignarEntrevista}>
                    Asignar Entrevista
                  </Button>
                )}
                <TextField
                  label="Enlace o información de la reunión de Google Meet"
                  fullWidth
                  value={inputEnlace}
                  onChange={handleEnlaceChange}
                  disabled={seguimiento.entrevista.realizada}
                />
                {infoReunion && (
                  <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Campo</TableCell>
                          <TableCell>Valor</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {infoReunion.titulo && (
                          <TableRow>
                            <TableCell>Título</TableCell>
                            <TableCell>{infoReunion.titulo}</TableCell>
                          </TableRow>
                        )}
                        {infoReunion.fechaTexto && (
                          <TableRow>
                            <TableCell>Fecha</TableCell>
                            <TableCell>{infoReunion.fechaTexto}</TableCell>
                          </TableRow>
                        )}
                        {infoReunion.zonaHoraria && (
                          <TableRow>
                            <TableCell>Zona Horaria</TableCell>
                            <TableCell>{infoReunion.zonaHoraria}</TableCell>
                          </TableRow>
                        )}
                        {infoReunion.enlace && (
                          <TableRow>
                            <TableCell>Enlace</TableCell>
                            <TableCell>
                              <a href={infoReunion.enlace} target="_blank" rel="noopener noreferrer">
                                {infoReunion.enlace}
                              </a>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                {seguimiento.entrevista.fecha && (
                  <Box mt={2}>
                    <Typography>
                      Fecha y Hora de la Entrevista:{" "}
                      <strong>{seguimiento.entrevista.fecha.toLocaleString()}</strong>
                    </Typography>
                  </Box>
                )}
                {seguimiento.entrevista.enlace && (
                  <Button
                    variant="outlined"
                    color="primary"
                    href={infoReunion && infoReunion.enlace ? infoReunion.enlace : "#"}
                    target="_blank"
                    disabled={!infoReunion || !infoReunion.enlace}
                    sx={{ mt: 2, mb: 2, ml: 2, mr: 2 }}
                  >
                    Ir a la Entrevista
                  </Button>
                )}
                {infoReunion && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleEnviarEntrevista}
                    sx={{ mt: 2, mb: 2, ml: 2, mr: 2 }}
                  >
                    Enviar Entrevista
                  </Button>
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={seguimiento.entrevista.realizada}
                      onChange={() => handleCheckboxChange("entrevista.realizada")}
                    />
                  }
                  label="Realizada"
                />
                <TextField
                  label="Reporte de Entrevista"
                  multiline
                  rows={4}
                  fullWidth
                  value={seguimiento.entrevista.reporte}
                  onChange={(e) => handleInputChange("entrevista.reporte", e.target.value)}
                  disabled={!seguimiento.entrevista.realizada}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={seguimiento.entrevista.aprobado}
                      onChange={() => handleCheckboxChange("entrevista.aprobado")}
                      disabled={!seguimiento.entrevista.realizada}
                    />
                  }
                  label="Aprobado"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Documentación
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={seguimiento.documentacion.recibida}
                      onChange={() => handleCheckboxChange("documentacion.recibida")}
                    />
                  }
                  label="Recibida"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={seguimiento.documentacion.verificada}
                      onChange={() => handleCheckboxChange("documentacion.verificada")}
                      disabled={!seguimiento.documentacion.recibida}
                    />
                  }
                  label="Verificada"
                />
                <TextField
                  label="Observaciones"
                  multiline
                  rows={4}
                  fullWidth
                  value={seguimiento.documentacion.observaciones}
                  onChange={(e) => handleInputChange("documentacion.observaciones", e.target.value)}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleEnviarSolicitud}
                  sx={{ mt: 2, mb: 2, ml: 2, mr: 2 }}
                >
                  Enviar Solicitud
                </Button>
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom>
                    Documentos enviados por el solicitante
                  </Typography>
                  {postulacion.archivos && postulacion.archivos.cvUrl ? (
                    <Box mb={2}>
                      <Typography variant="subtitle1">CV:</Typography>
                      <a
                        href={postulacion.archivos.cvUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: "none", color: "blue" }}
                      >
                        Ver CV
                      </a>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleDeleteCV}
                        sx={{ ml: 2 }}
                      >
                        Eliminar CV
                      </Button>
                      <Button variant="contained" component="label" sx={{ ml: 2 }}>
                        Actualizar CV
                        <input type="file" hidden onChange={handleUpdateCV} />
                      </Button>
                    </Box>
                  ) : (
                    <Typography variant="body2">No se ha subido un CV.</Typography>
                  )}
                  {postulacion.archivos &&
                  postulacion.archivos.certificadosUrls &&
                  postulacion.archivos.certificadosUrls.length > 0 ? (
                    <Box>
                      <Typography variant="subtitle1">Certificados:</Typography>
                      {postulacion.archivos.certificadosUrls.map((url, index) => (
                        <Box key={index} display="flex" alignItems="center" mb={1}>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "none", color: "blue" }}
                          >
                            Ver Certificado {index + 1}
                          </a>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => handleDeleteCertificado(index)}
                            sx={{ ml: 2 }}
                          >
                            Eliminar
                          </Button>
                          <Button variant="contained" component="label" sx={{ ml: 2 }}>
                            Actualizar Certificado
                            <input
                              type="file"
                              hidden
                              onChange={(e) => handleUpdateCertificado(index, e)}
                            />
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2">No se han subido certificados.</Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
            <Box mt={2}>
              <Typography>
                Estado actual: <strong>{postulacion.estado || seguimiento.estado}</strong>
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleContratar} color="success">
              Contratar
            </Button>
            <Button onClick={handleNoContinuar} color="error">
              No Continuar
            </Button>
            <Button onClick={onClose}>Cancelar</Button>
            <Button onClick={handleSaveSeguimiento} variant="contained">
              Guardar
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
